import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import { tasksService, projectsService, usersService } from "@/services/api";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Search, 
  Plus, 
  List, 
  LayoutGrid, 
  User,
  X,
  CheckSquare,
  Clock,
  AlertCircle,
  Calendar,
  FolderKanban
} from "lucide-react";
import TaskCard from "@/components/TaskCard";
import TaskBoard from "@/components/TaskBoard";
import TaskDetailPanel from "@/components/TaskDetailPanel";
import NewTaskPanel from "@/components/NewTaskPanel";
import { cn } from "@/lib/utils";

const STATUSES = ["To Do", "In Progress", "In Review", "Blocked", "Completed", "Pending Approval"];
const PRIORITIES = ["Urgent", "High", "Medium", "Low"];
const GROUP_BY_OPTIONS = [
  { value: "none", label: "No Grouping" },
  { value: "project", label: "Group by Project" },
  { value: "status", label: "Group by Status" },
  { value: "priority", label: "Group by Priority" },
  { value: "assignee", label: "Group by Assignee" }
];
const SORT_OPTIONS = [
  { value: "due-soonest", label: "Due Date (Soonest)" },
  { value: "due-latest", label: "Due Date (Latest)" },
  { value: "priority-high", label: "Priority (Highest)" },
  { value: "priority-low", label: "Priority (Lowest)" },
  { value: "newest", label: "Date Created (Newest)" },
  { value: "oldest", label: "Date Created (Oldest)" },
  { value: "alphabetical", label: "Alphabetical (A-Z)" }
];
const DUE_DATE_OPTIONS = ["All", "Today", "This Week", "This Month", "Overdue", "Custom"];

const Tasks = () => {
  const { userData } = useAuth();
  const { toast } = useToast();
  
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("list");
  const [myTasksView, setMyTasksView] = useState(false);
  const [statusFilter, setStatusFilter] = useState("All");
  const [priorityFilter, setPriorityFilter] = useState("All");
  const [projectFilter, setProjectFilter] = useState("All");
  const [assigneeFilter, setAssigneeFilter] = useState("All");
  const [dueDateFilter, setDueDateFilter] = useState("All");
  const [sortBy, setSortBy] = useState("due-soonest");
  const [groupBy, setGroupBy] = useState("none");
  
  const [selectedTask, setSelectedTask] = useState(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isNewTaskOpen, setIsNewTaskOpen] = useState(false);
  const [newTaskStatus, setNewTaskStatus] = useState("To Do");
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 20;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [tasksData, projectsData, usersData] = await Promise.all([
          tasksService.getAll(),
          projectsService.getAll(),
          usersService.getAll()
        ]);
        setTasks(tasksData);
        setProjects(projectsData);
        setTeamMembers(usersData);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await tasksService.update(taskId, { status: newStatus });
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: newStatus } : t));
      toast({ title: "Status updated", description: `Task moved to ${newStatus}`, variant: "success" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to update status", variant: "destructive" });
    }
  };

  const handleComplete = async (taskId, newStatus) => {
    try {
      await tasksService.update(taskId, { status: newStatus });
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: newStatus } : t));
      toast({ title: "Task completed", description: "Great job!", variant: "success" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to update task", variant: "destructive" });
    }
  };

  const handleDeleteTask = async (task) => {
    try {
      await tasksService.delete(task.id);
      setTasks(prev => prev.filter(t => t.id !== task.id));
      toast({ title: "Task deleted", variant: "success" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete task", variant: "destructive" });
    }
  };

  const handleDuplicateTask = async (task) => {
    try {
      const { id, ...taskData } = task;
      await tasksService.create({ ...taskData, title: `${task.title} (Copy)`, status: "To Do" });
      const newTasks = await tasksService.getAll();
      setTasks(newTasks);
      toast({ title: "Task duplicated", variant: "success" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to duplicate task", variant: "destructive" });
    }
  };

  const handleTaskCreated = () => {
    const fetchTasks = async () => {
      const data = await tasksService.getAll();
      setTasks(data);
    };
    fetchTasks();
  };

  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("All");
    setPriorityFilter("All");
    setProjectFilter("All");
    setAssigneeFilter("All");
    setDueDateFilter("All");
    setSortBy("due-soonest");
    setMyTasksView(false);
  };

  const filteredTasks = useMemo(() => {
    let result = [...tasks];

    const currentUserId = userData?.id;
    const userRole = userData?.role;
    const isLeadership = userRole === "COO" || userRole === "Digital Marketing Manager" || userRole === "Client Relations Team Lead" || userRole === "CEO";

    result = result.filter(t => {
      if (t.status === "Pending Approval") {
        return t.assignedTo === currentUserId || isLeadership;
      }
      return true;
    });

    if (myTasksView && currentUserId) {
      result = result.filter(t => t.assignedTo === currentUserId);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(t => 
        t.title?.toLowerCase().includes(term) || 
        t.description?.toLowerCase().includes(term)
      );
    }

    if (statusFilter !== "All") {
      result = result.filter(t => t.status === statusFilter);
    }

    if (priorityFilter !== "All") {
      result = result.filter(t => t.priority === priorityFilter);
    }

    if (projectFilter !== "All") {
      result = result.filter(t => t.projectId === projectFilter);
    }

    if (assigneeFilter !== "All") {
      result = result.filter(t => t.assignedTo === assigneeFilter);
    }

    if (dueDateFilter !== "All") {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const weekEnd = new Date(today);
      weekEnd.setDate(weekEnd.getDate() + 7);
      const monthEnd = new Date(today);
      monthEnd.setMonth(monthEnd.getMonth() + 1);

      result = result.filter(t => {
        if (!t.dueDate) return dueDateFilter === "All";
        const dueDate = new Date(t.dueDate);
        
        switch (dueDateFilter) {
          case "Today":
            return dueDate.toDateString() === today.toDateString();
          case "This Week":
            return dueDate >= today && dueDate <= weekEnd;
          case "This Month":
            return dueDate >= today && dueDate <= monthEnd;
          case "Overdue":
            return dueDate < today && t.status !== "Completed";
          default:
            return true;
        }
      });
    }

    switch (sortBy) {
      case "due-soonest":
        result.sort((a, b) => new Date(a.dueDate || 9999999999999) - new Date(b.dueDate || 9999999999999));
        break;
      case "due-latest":
        result.sort((a, b) => new Date(b.dueDate || 0) - new Date(a.dueDate || 0));
        break;
      case "priority-high":
        const priorityOrder = { "Urgent": 0, "High": 1, "Medium": 2, "Low": 3 };
        result.sort((a, b) => (priorityOrder[a.priority] || 3) - (priorityOrder[b.priority] || 3));
        break;
      case "priority-low":
        const priorityOrderLow = { "Urgent": 3, "High": 2, "Medium": 1, "Low": 0 };
        result.sort((a, b) => (priorityOrderLow[a.priority] || 0) - (priorityOrderLow[b.priority] || 0));
        break;
      case "newest":
        result.sort((a, b) => new Date(b.createdAt?.toDate?.() || 0) - new Date(a.createdAt?.toDate?.() || 0));
        break;
      case "oldest":
        result.sort((a, b) => new Date(a.createdAt?.toDate?.() || 0) - new Date(b.createdAt?.toDate?.() || 0));
        break;
      case "alphabetical":
        result.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
        break;
      default:
        break;
    }

    return result;
  }, [tasks, searchTerm, statusFilter, priorityFilter, projectFilter, assigneeFilter, dueDateFilter, sortBy, myTasksView, userData]);

  const stats = useMemo(() => {
    const currentUserId = userData?.id;
    const myTasks = currentUserId ? tasks.filter(t => t.assignedTo === currentUserId) : [];
    const inProgress = tasks.filter(t => t.status === "In Progress").length;
    const completedToday = tasks.filter(t => {
      if (t.status !== "Completed") return false;
      const updated = t.updatedAt?.toDate?.();
      if (!updated) return false;
      const today = new Date();
      return updated.toDateString() === today.toDateString();
    }).length;
    const overdue = tasks.filter(t => 
      t.dueDate && new Date(t.dueDate) < new Date() && t.status !== "Completed"
    ).length;
    const dueThisWeek = tasks.filter(t => {
      if (!t.dueDate || t.status === "Completed") return false;
      const dueDate = new Date(t.dueDate);
      const today = new Date();
      const weekEnd = new Date(today);
      weekEnd.setDate(weekEnd.getDate() + 7);
      return dueDate >= today && dueDate <= weekEnd;
    }).length;

    return {
      total: tasks.length,
      myTasks: myTasks.length,
      inProgress,
      completedToday,
      overdue,
      dueThisWeek
    };
  }, [tasks, userData]);

  const handleStatClick = (stat) => {
    if (stat === "myTasks") {
      setMyTasksView(true);
      setViewMode("list");
    } else if (stat === "inProgress") {
      setStatusFilter("In Progress");
      setMyTasksView(false);
    } else if (stat === "overdue") {
      setDueDateFilter("Overdue");
      setMyTasksView(false);
    } else if (stat === "dueThisWeek") {
      setDueDateFilter("This Week");
      setMyTasksView(false);
    } else {
      setMyTasksView(false);
      setStatusFilter("All");
      setDueDateFilter("All");
    }
  };

  const paginatedTasks = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredTasks.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredTasks, currentPage]);

  const totalPages = Math.ceil(filteredTasks.length / ITEMS_PER_PAGE);

  const hasActiveFilters = searchTerm || statusFilter !== "All" || priorityFilter !== "All" || 
    projectFilter !== "All" || assigneeFilter !== "All" || dueDateFilter !== "All" || myTasksView;

  const groupedTasks = useMemo(() => {
    if (groupBy === "none") return null;
    
    const groups = {};
    filteredTasks.forEach(task => {
      let key;
      switch (groupBy) {
        case "project":
          key = projects.find(p => p.id === task.projectId)?.name || "No Project";
          break;
        case "status":
          key = task.status || "To Do";
          break;
        case "priority":
          key = task.priority || "Medium";
          break;
        case "assignee":
          key = teamMembers.find(m => m.id === task.assignedTo)?.name || "Unassigned";
          break;
        default:
          key = "All";
      }
      if (!groups[key]) groups[key] = [];
      groups[key].push(task);
    });
    return groups;
  }, [filteredTasks, groupBy, projects, teamMembers]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between">
          <div className="space-y-2">
            <Skeleton className="h-8 w-24" />
            <Skeleton className="h-4 w-64" />
          </div>
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Skeleton key={i} className="h-20 w-full rounded-xl" />
          ))}
        </div>
        <Skeleton className="h-14 w-full" />
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map(i => (
            <Skeleton key={i} className="h-16 w-full rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-navy">Tasks</h1>
          <p className="text-gray-500 mt-1 text-sm sm:text-base">Manage and track all tasks</p>
        </div>
        <Button onClick={() => setIsNewTaskOpen(true)} className="bg-navy hover:bg-navy-light w-full sm:w-auto">
          <Plus className="w-4 h-4 mr-2" />
          <span className="sm:hidden">New</span>
          <span className="hidden sm:inline">New Task</span>
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-4">
        <button onClick={() => handleStatClick("total")} className="bg-white p-2 sm:p-4 rounded-xl shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <CheckSquare className="w-4 h-4 text-blue-600" />
            </div>
            <div className="text-left">
              <div className="text-lg sm:text-2xl font-bold text-navy">{stats.total}</div>
              <div className="text-xs text-gray-500 hidden sm:block">Total Tasks</div>
            </div>
          </div>
        </button>
        <button onClick={() => handleStatClick("myTasks")} className="bg-white p-2 sm:p-4 rounded-xl shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <User className="w-4 h-4 text-purple-600" />
            </div>
            <div className="text-left">
              <div className="text-lg sm:text-2xl font-bold text-navy">{stats.myTasks}</div>
              <div className="text-xs text-gray-500 hidden sm:block">My Tasks</div>
            </div>
          </div>
        </button>
        <button onClick={() => handleStatClick("inProgress")} className="bg-white p-2 sm:p-4 rounded-xl shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Clock className="w-4 h-4 text-blue-600" />
            </div>
            <div className="text-left">
              <div className="text-lg sm:text-2xl font-bold text-navy">{stats.inProgress}</div>
              <div className="text-xs text-gray-500 hidden sm:block">In Progress</div>
            </div>
          </div>
        </button>
        <button onClick={() => handleStatClick("completedToday")} className="bg-white p-2 sm:p-4 rounded-xl shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckSquare className="w-4 h-4 text-green-600" />
            </div>
            <div className="text-left">
              <div className="text-2xl font-bold text-navy">{stats.completedToday}</div>
              <div className="text-xs text-gray-500">Completed Today</div>
            </div>
          </div>
        </button>
        <button onClick={() => handleStatClick("overdue")} className={cn("p-4 rounded-xl shadow-sm hover:shadow-md transition-shadow", stats.overdue > 0 ? "bg-red-50" : "bg-white")}>
          <div className="flex items-center gap-3">
            <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", stats.overdue > 0 ? "bg-red-100" : "bg-slate-100")}>
              <AlertCircle className={cn("w-5 h-5", stats.overdue > 0 ? "text-red-600" : "text-slate-400")} />
            </div>
            <div className="text-left">
              <div className={cn("text-2xl font-bold", stats.overdue > 0 ? "text-red-600" : "text-navy")}>{stats.overdue}</div>
              <div className="text-xs text-gray-500">Overdue</div>
            </div>
          </div>
        </button>
        <button onClick={() => handleStatClick("dueThisWeek")} className="bg-white p-4 rounded-xl shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 text-amber-600" />
            </div>
            <div className="text-left">
              <div className="text-2xl font-bold text-navy">{stats.dueThisWeek}</div>
              <div className="text-xs text-gray-500">Due This Week</div>
            </div>
          </div>
        </button>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-2 lg:gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search tasks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-slate-200 text-sm"
              />
            </div>

            <div className="flex flex-wrap gap-1 sm:gap-2">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-2 sm:px-3 py-2 border border-slate-200 rounded-lg text-xs sm:text-sm"
              >
                <option value="All">Status</option>
                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>

              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="px-2 sm:px-3 py-2 border border-slate-200 rounded-lg text-xs sm:text-sm"
              >
                <option value="All">Priority</option>
                {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
              </select>

              <select
                value={projectFilter}
                onChange={(e) => setProjectFilter(e.target.value)}
                className="px-2 sm:px-3 py-2 border border-slate-200 rounded-lg text-xs sm:text-sm"
              >
                <option value="All">Project</option>
                {projects.filter(p => p.status !== "Archived").map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>

              <select
                value={assigneeFilter}
                onChange={(e) => setAssigneeFilter(e.target.value)}
                className="px-2 sm:px-3 py-2 border border-slate-200 rounded-lg text-xs sm:text-sm"
              >
                <option value="All">Assignee</option>
                {teamMembers.map(m => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>

              <select
                value={dueDateFilter}
                onChange={(e) => setDueDateFilter(e.target.value)}
                className="px-2 sm:px-3 py-2 border border-slate-200 rounded-lg text-xs sm:text-sm"
              >
                {DUE_DATE_OPTIONS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>

              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-2 sm:px-3 py-2 border border-slate-200 rounded-lg text-xs sm:text-sm"
              >
                {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>

              <select
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value)}
                className="px-2 sm:px-3 py-2 border border-slate-200 rounded-lg text-xs sm:text-sm"
              >
                {GROUP_BY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>

            <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
              <Button
                variant={viewMode === "list" ? "default" : "ghost"}
                size="icon"
                onClick={() => { setViewMode("list"); setMyTasksView(false); }}
                className={viewMode === "list" ? "bg-navy text-white" : "text-gray-500"}
              >
                <List className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === "board" ? "default" : "ghost"}
                size="icon"
                onClick={() => { setViewMode("board"); setMyTasksView(false); }}
                className={viewMode === "board" ? "bg-navy text-white" : "text-gray-500"}
              >
                <LayoutGrid className="w-4 h-4" />
              </Button>
              <Button
                variant={myTasksView ? "default" : "ghost"}
                size="icon"
                onClick={() => { setMyTasksView(!myTasksView); setViewMode("list"); }}
                className={myTasksView ? "bg-navy text-white" : "text-gray-500"}
              >
                <User className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {hasActiveFilters && (
            <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2 flex-wrap">
              <span className="text-sm text-gray-500">Active filters:</span>
              {searchTerm && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded text-sm">
                  Search: {searchTerm}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setSearchTerm("")} />
                </span>
              )}
              {statusFilter !== "All" && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded text-sm">
                  Status: {statusFilter}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setStatusFilter("All")} />
                </span>
              )}
              {myTasksView && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 rounded text-sm text-purple-700">
                  My Tasks
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setMyTasksView(false)} />
                </span>
              )}
              <Button variant="link" size="sm" onClick={clearFilters} className="text-action-blue">
                Clear all
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {filteredTasks.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckSquare className="w-10 h-10 text-slate-400" />
            </div>
            <h3 className="text-xl font-bold text-navy mb-2">
              {hasActiveFilters ? "No Tasks Match Your Filters" : "No Tasks Yet"}
            </h3>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">
              {hasActiveFilters 
                ? "Try adjusting your search or filters to find what you're looking for."
                : "Create your first task or assign tasks from your projects to get started."
              }
            </p>
            {hasActiveFilters ? (
              <Button variant="outline" onClick={clearFilters}>
                Clear Filters
              </Button>
            ) : (
              <Button onClick={() => setIsNewTaskOpen(true)} className="bg-navy hover:bg-navy-light">
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Task
              </Button>
            )}
          </CardContent>
        </Card>
      ) : viewMode === "board" ? (
        <TaskBoard
          tasks={filteredTasks}
          projects={projects}
          teamMembers={teamMembers}
          onStatusChange={handleStatusChange}
          onTaskClick={(task) => { setSelectedTask(task); setIsPanelOpen(true); }}
          onAddTask={(status) => { setNewTaskStatus(status); setIsNewTaskOpen(true); }}
          onComplete={handleComplete}
        />
      ) : groupedTasks ? (
        <div className="space-y-6">
          {Object.entries(groupedTasks).map(([groupName, groupTasks]) => (
            <div key={groupName}>
              <div className="flex items-center gap-2 mb-3">
                <h3 className="font-semibold text-navy">{groupName}</h3>
                <span className="text-sm text-gray-500">({groupTasks.length})</span>
              </div>
              <div className="space-y-2">
                {groupTasks.map(task => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    projects={projects}
                    teamMembers={teamMembers}
                    onStatusChange={handleStatusChange}
                    onComplete={handleComplete}
                    onDelete={handleDeleteTask}
                    onDuplicate={handleDuplicateTask}
                    onClick={(t) => { setSelectedTask(t); setIsPanelOpen(true); }}
                    isSelected={selectedTasks.includes(task.id)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <>
          <div className="space-y-2">
            {paginatedTasks.map(task => (
              <TaskCard
                key={task.id}
                task={task}
                projects={projects}
                teamMembers={teamMembers}
                onStatusChange={handleStatusChange}
                onComplete={handleComplete}
                onDelete={handleDeleteTask}
                onDuplicate={handleDuplicateTask}
                onClick={(t) => { setSelectedTask(t); setIsPanelOpen(true); }}
                isSelected={selectedTasks.includes(task.id)}
              />
            ))}
          </div>
          {totalPages > 1 && (
            <div className="flex justify-center gap-2">
              <Button variant="outline" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>
                Previous
              </Button>
              {Array.from({ length: totalPages }, (_, i) => (
                <Button key={i + 1} variant={currentPage === i + 1 ? "default" : "outline"} onClick={() => setCurrentPage(i + 1)} className={currentPage === i + 1 ? "bg-navy" : ""}>
                  {i + 1}
                </Button>
              ))}
              <Button variant="outline" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>
                Next
              </Button>
            </div>
          )}
        </>
      )}

      <TaskDetailPanel
        task={selectedTask}
        projects={projects}
        teamMembers={teamMembers}
        isOpen={isPanelOpen}
        onClose={() => { setIsPanelOpen(false); setSelectedTask(null); handleTaskCreated(); }}
        onUpdate={handleTaskCreated}
      />

      <NewTaskPanel
        isOpen={isNewTaskOpen}
        onClose={() => setIsNewTaskOpen(false)}
        onSuccess={handleTaskCreated}
        initialStatus={newTaskStatus}
        projects={projects}
        teamMembers={teamMembers}
      />
    </div>
  );
};

export default Tasks;